import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { startOfDay, endOfDay, subDays, differenceInMinutes } from 'date-fns';

interface DateRange {
  from: Date | null;
  to: Date | null;
}

interface UserUsageMetrics {
  userId: string;
  userName: string;
  userEmail: string;
  avatarUrl: string | null;
  totalMinutes: number;
  sessionsCount: number;
  avgSessionMinutes: number;
  isOnline: boolean;
  lastActivity: string | null;
  currentPath: string | null;
}

interface PageMetrics {
  path: string;
  visits: number;
  uniqueUsers: number;
}

interface HourlyMetrics {
  hour: number;
  activeUsers: number;
}

interface SessionInterval {
  start: Date;
  end: Date;
}

// Helper function to merge overlapping intervals
function mergeOverlappingIntervals(intervals: SessionInterval[]): SessionInterval[] {
  if (intervals.length === 0) return [];
  
  // Sort by start time
  const sorted = [...intervals].sort((a, b) => a.start.getTime() - b.start.getTime());
  
  const merged: SessionInterval[] = [sorted[0]];
  
  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    const last = merged[merged.length - 1];
    
    // If current overlaps with last, merge them
    if (current.start.getTime() <= last.end.getTime()) {
      last.end = new Date(Math.max(last.end.getTime(), current.end.getTime()));
    } else {
      merged.push(current);
    }
  }
  
  return merged;
}

// Helper function to calculate total minutes from merged intervals
function calculateTotalMinutesFromIntervals(intervals: SessionInterval[]): number {
  const merged = mergeOverlappingIntervals(intervals);
  return merged.reduce((total, interval) => {
    return total + Math.max(0, differenceInMinutes(interval.end, interval.start));
  }, 0);
}

// Helper to check if a session is a "ghost session" (heartbeat too old)
function isGhostSession(lastHeartbeat: string | null, thresholdMinutes: number = 5): boolean {
  if (!lastHeartbeat) return true;
  const heartbeatTime = new Date(lastHeartbeat).getTime();
  const now = Date.now();
  const diffMinutes = (now - heartbeatTime) / (1000 * 60);
  return diffMinutes > thresholdMinutes;
}

export function useUsageMetrics(dateRange?: DateRange) {
  const from = dateRange?.from || subDays(new Date(), 30);
  const to = dateRange?.to || new Date();

  // Fetch user usage data with improved overlapping session handling
  const { data: userMetrics, isLoading: loadingUsers, refetch: refetchUsers } = useQuery({
    queryKey: ['usage-metrics-users', from?.toISOString(), to?.toISOString()],
    queryFn: async () => {
      // Fetch sessions within date range
      const { data: sessions, error: sessionsError } = await supabase
        .from('user_sessions')
        .select(`
          id,
          user_id,
          session_started_at,
          session_ended_at,
          last_heartbeat_at,
          is_active,
          app_users!user_sessions_user_id_fkey (
            id,
            display_name,
            first_name,
            last_name,
            email,
            avatar_url,
            is_online,
            last_activity_at
          )
        `)
        .gte('session_started_at', startOfDay(from).toISOString())
        .lte('session_started_at', endOfDay(to).toISOString())
        .order('session_started_at', { ascending: false });

      if (sessionsError) throw sessionsError;

      // Group sessions by user with interval tracking
      const userSessionsMap = new Map<string, {
        user: any;
        intervals: SessionInterval[];
        sessionsCount: number;
      }>();

      sessions?.forEach((session: any) => {
        const user = session.app_users;
        if (!user) return;

        const userId = user.id;
        const sessionStart = new Date(session.session_started_at);
        
        // Determine session end time
        let sessionEnd: Date;
        if (session.session_ended_at) {
          sessionEnd = new Date(session.session_ended_at);
        } else if (session.is_active && !isGhostSession(session.last_heartbeat_at)) {
          // Active session with recent heartbeat - use current time
          sessionEnd = new Date();
        } else {
          // Ghost session or ended - use last heartbeat
          sessionEnd = new Date(session.last_heartbeat_at || session.session_started_at);
        }

        if (!userSessionsMap.has(userId)) {
          userSessionsMap.set(userId, {
            user,
            intervals: [],
            sessionsCount: 0,
          });
        }

        const userData = userSessionsMap.get(userId)!;
        userData.intervals.push({ start: sessionStart, end: sessionEnd });
        userData.sessionsCount += 1;
      });

      // Calculate metrics with merged intervals
      const result: UserUsageMetrics[] = [];
      
      userSessionsMap.forEach((data, userId) => {
        const totalMinutes = calculateTotalMinutesFromIntervals(data.intervals);
        
        result.push({
          userId,
          userName: data.user.display_name || `${data.user.first_name || ''} ${data.user.last_name || ''}`.trim() || 'Unknown',
          userEmail: data.user.email || '',
          avatarUrl: data.user.avatar_url,
          totalMinutes,
          sessionsCount: data.sessionsCount,
          avgSessionMinutes: data.sessionsCount > 0 ? Math.round(totalMinutes / data.sessionsCount) : 0,
          isOnline: data.user.is_online || false,
          lastActivity: data.user.last_activity_at,
          currentPath: null,
        });
      });

      return result.sort((a, b) => b.totalMinutes - a.totalMinutes);
    },
    refetchInterval: 60000, // Refetch every minute
  });

  // Fetch active sessions (online users) - now filtering ghost sessions and deduplicating users
  const { data: activeSessions, isLoading: loadingActive, refetch: refetchActive } = useQuery({
    queryKey: ['usage-metrics-active'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_sessions')
        .select(`
          id,
          user_id,
          session_started_at,
          last_heartbeat_at,
          app_users!user_sessions_user_id_fkey (
            id,
            display_name,
            first_name,
            last_name,
            avatar_url
          )
        `)
        .eq('is_active', true)
        .order('last_heartbeat_at', { ascending: false });

      if (error) throw error;

      // Filter out ghost sessions (heartbeat > 5 minutes ago)
      const validSessions = data?.filter((session: any) => 
        !isGhostSession(session.last_heartbeat_at)
      ) || [];

      // Get current paths from latest heartbeats
      const sessionIds = validSessions.map(s => s.id);
      
      if (sessionIds.length === 0) return [];

      const { data: heartbeats } = await supabase
        .from('user_activity_heartbeats')
        .select('session_id, current_path, timestamp')
        .in('session_id', sessionIds)
        .order('timestamp', { ascending: false });

      // Get latest path for each session
      const pathMap = new Map<string, string>();
      heartbeats?.forEach((hb: any) => {
        if (!pathMap.has(hb.session_id)) {
          pathMap.set(hb.session_id, hb.current_path);
        }
      });

      // Deduplicate by user - keep only the most recent session per user
      const userSessionMap = new Map<string, any>();
      
      validSessions.forEach((session: any) => {
        const userId = session.user_id;
        const existingSession = userSessionMap.get(userId);
        
        // Keep the session with the most recent heartbeat
        if (!existingSession || 
            new Date(session.last_heartbeat_at) > new Date(existingSession.last_heartbeat_at)) {
          userSessionMap.set(userId, session);
        }
      });

      // Convert to array with user info
      return Array.from(userSessionMap.values()).map((session: any) => ({
        sessionId: session.id,
        userId: session.user_id,
        userName: session.app_users?.display_name || 
          `${session.app_users?.first_name || ''} ${session.app_users?.last_name || ''}`.trim() || 'Unknown',
        avatarUrl: session.app_users?.avatar_url,
        sessionStartedAt: session.session_started_at,
        lastHeartbeat: session.last_heartbeat_at,
        currentPath: pathMap.get(session.id) || '/',
      }));
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Fetch page metrics
  const { data: pageMetrics, isLoading: loadingPages } = useQuery({
    queryKey: ['usage-metrics-pages', from?.toISOString(), to?.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_activity_heartbeats')
        .select('current_path, user_id')
        .gte('timestamp', startOfDay(from).toISOString())
        .lte('timestamp', endOfDay(to).toISOString());

      if (error) throw error;

      // Group by path
      const pathMap = new Map<string, { visits: number; users: Set<string> }>();
      
      data?.forEach((hb: any) => {
        if (!pathMap.has(hb.current_path)) {
          pathMap.set(hb.current_path, { visits: 0, users: new Set() });
        }
        const pathData = pathMap.get(hb.current_path)!;
        pathData.visits += 1;
        pathData.users.add(hb.user_id);
      });

      const result: PageMetrics[] = [];
      pathMap.forEach((value, path) => {
        result.push({
          path,
          visits: value.visits,
          uniqueUsers: value.users.size,
        });
      });

      return result.sort((a, b) => b.visits - a.visits).slice(0, 10);
    },
  });

  // Fetch hourly distribution
  const { data: hourlyMetrics, isLoading: loadingHourly } = useQuery({
    queryKey: ['usage-metrics-hourly', from?.toISOString(), to?.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_activity_heartbeats')
        .select('timestamp, user_id')
        .gte('timestamp', startOfDay(from).toISOString())
        .lte('timestamp', endOfDay(to).toISOString());

      if (error) throw error;

      // Group by hour
      const hourMap = new Map<number, Set<string>>();
      
      for (let i = 0; i < 24; i++) {
        hourMap.set(i, new Set());
      }

      data?.forEach((hb: any) => {
        const hour = new Date(hb.timestamp).getHours();
        hourMap.get(hour)?.add(hb.user_id);
      });

      const result: HourlyMetrics[] = [];
      hourMap.forEach((users, hour) => {
        result.push({
          hour,
          activeUsers: users.size,
        });
      });

      return result.sort((a, b) => a.hour - b.hour);
    },
  });

  // Summary stats - now using deduplicated active sessions count
  const summary = {
    onlineNow: activeSessions?.length || 0,
    totalUsers: userMetrics?.length || 0,
    totalMinutesToday: userMetrics?.reduce((sum, u) => sum + u.totalMinutes, 0) || 0,
    totalSessions: userMetrics?.reduce((sum, u) => sum + u.sessionsCount, 0) || 0,
    avgSessionMinutes: userMetrics && userMetrics.length > 0
      ? Math.round(userMetrics.reduce((sum, u) => sum + u.avgSessionMinutes, 0) / userMetrics.length)
      : 0,
  };

  return {
    userMetrics: userMetrics || [],
    activeSessions: activeSessions || [],
    pageMetrics: pageMetrics || [],
    hourlyMetrics: hourlyMetrics || [],
    summary,
    isLoading: loadingUsers || loadingActive || loadingPages || loadingHourly,
    refetch: () => {
      refetchUsers();
      refetchActive();
    },
  };
}
